import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Grid, CircularProgress } from '@mui/material';
import * as T from '../../components/Typography';
import * as S from './style';
import DownloadSurveyCard from '../../components/DownloadSurveyCard';
import SupportModal from '../../components/SupportModal';
import { useAuth } from '../../context/auth';

import { routes } from '../../constants';
import { useGetUserSurvey } from '../../api-calls/survey.queries';

const Survey = () => {
  const location = useLocation();
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const sectionOrder = queryParams.get('sectionOrder') || '0';

  const { user } = useAuth();

  const { data, isError, error, isLoading } = useGetUserSurvey({
    salesforceContactId: user?.salesforceContactId
  });

  // Event listener for the postMessage from the iframe
  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      if (
        event.origin === import.meta.env.VITE_SF_SURVEY_URL &&
        event.data.eventType === 'formSubmitted' &&
        event.data.data.contact === user?.salesforceContactId
      ) {
        navigate(routes.COMPLETE_PAYMENT);
      } else {
        console.error('Invalid postMessage event');
        return;
      }
    };

    window.addEventListener('message', handlePostMessage);

    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, [navigate, user?.salesforceContactId]);

  if (isLoading) {
    return (
      <Box
        width={'100%'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        data-testid="loading"
      >
        <CircularProgress color={'secondary'} />
      </Box>
    );
  }

  if (isError) {
    return <T.H4 color="red">Error: {error?.message}</T.H4>;
  }

  const iframeSrcUrl = `${import.meta.env.VITE_SF_SURVEY_URL}/survey?&section=${sectionOrder}&token=${data?.userSurveyToken}`;

  return (
    <S.SurveyWrapper container spacing={2}>
      <Grid item xs={12} sm={8}>
        <Box mt={6}>
          <Box height={'70vh'} mb={2}>
            {isIframeLoading && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bgcolor="white"
                zIndex={1}
              >
                <CircularProgress />
              </Box>
            )}
            <iframe
              src={iframeSrcUrl}
              width="100%"
              height="100%"
              title="Survey Form"
              sandbox="allow-same-origin allow-scripts allow-forms allow-modals allow-popups allow-top-navigation-by-user-activation"
              style={{
                border: 'none',
                overflow: 'hidden',
                visibility: isIframeLoading ? 'hidden' : 'visible'
              }}
              onLoad={() => setIsIframeLoading(false)}
            />
          </Box>
        </Box>
        <S.StyledButton
          onClick={() =>
            navigate(routes.HOME, { state: { needsRefetch: true } })
          }
        >
          <T.Link weight={'bold'} decoration={'none'} mr={5}>
            Back to portal
          </T.Link>
        </S.StyledButton>
        <S.VerticalTextDivider>|</S.VerticalTextDivider>
        <T.Link
          weight={'bold'}
          decoration={'none'}
          mr={5}
          to={routes.FAQ}
          target="_blank"
        >
          FAQs
        </T.Link>
        <S.VerticalTextDivider>|</S.VerticalTextDivider>
        <T.Link
          weight={'bold'}
          decoration={'none'}
          mr={5}
          onClick={() => setShowSupportModal(true)}
        >
          Contact support
        </T.Link>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box mt={6}>
          <DownloadSurveyCard endDate={data?.endDate} />
        </Box>
      </Grid>
      <SupportModal
        visible={showSupportModal}
        setIsModalVisible={() => setShowSupportModal(!showSupportModal)}
      />
    </S.SurveyWrapper>
  );
};

export default Survey;
